import { useEffect, useState } from "react";
import { Bell, X } from "lucide-react";
import { toast } from "sonner";
import { enablePush, getPushSubscriptionStatus, isPushSupported, isIOSNonStandalone } from "@/lib/webPush";
import { useAuth } from "@/context/AuthContext";

/**
 * PushPermissionPrompt — banner non intrusivo che chiede all'utente di attivare
 * le notifiche push la PRIMA volta che accede (senza scomodarlo dopo).
 *
 * Stato salvato in localStorage:
 *   epicflow_push_prompt_state = "granted" | "dismissed" | "later"
 *
 * Il banner appare se:
 *   - Browser supporta Web Push (esclude iOS non-standalone)
 *   - Permesso Notifications non è ancora "granted" o "denied"
 *   - L'utente non ha già cliccato "Più tardi" nell'ultima settimana
 */
const STORAGE_KEY = "epicflow_push_prompt_state";
const STORAGE_TS_KEY = "epicflow_push_prompt_ts";
const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000; // 1 settimana

export default function PushPermissionPrompt() {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!user) return;  // Solo per utenti autenticati
      if (!isPushSupported()) return;
      if (isIOSNonStandalone()) return;  // iOS richiede PWA installata — skip auto-prompt

      const state = localStorage.getItem(STORAGE_KEY);
      if (state === "granted") return;

      // Snooze: mostra "Più tardi" solo dopo una settimana
      if (state === "later") {
        const ts = parseInt(localStorage.getItem(STORAGE_TS_KEY) || "0", 10);
        if (Date.now() - ts < SNOOZE_MS) return;
      }

      const s = await getPushSubscriptionStatus();
      if (s.permission === "granted" && s.subscribed) {
        localStorage.setItem(STORAGE_KEY, "granted");
        return;
      }
      if (s.permission === "denied") {
        localStorage.setItem(STORAGE_KEY, "denied");
        return;
      }
      // Delay minimo di 800ms per lasciare che si mostri prima il popup install
      const t = setTimeout(() => { if (mounted) setVisible(true); }, 800);
      return () => clearTimeout(t);
    })();
    return () => { mounted = false; };
  }, [user]);

  const activate = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await enablePush();
      localStorage.setItem(STORAGE_KEY, "granted");
      toast.success("Notifiche attive — riceverai messaggi anche quando l'app è chiusa");
      setVisible(false);
    } catch (e) {
      toast.error(e.message || "Impossibile attivare le notifiche");
      // Non salviamo "denied" qui — l'utente potrebbe voler riprovare
    } finally { setBusy(false); }
  };

  const later = () => {
    localStorage.setItem(STORAGE_KEY, "later");
    localStorage.setItem(STORAGE_TS_KEY, String(Date.now()));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="push-permission-prompt"
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-md z-40"
    >
      <div className="bg-card border border-primary/40 rounded-2xl p-4 shadow-2xl">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center flex-shrink-0">
            <Bell className="w-5 h-5 text-primary" strokeWidth={1.5} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-serif-display text-lg leading-tight">Vuoi essere avvisata?</div>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              Attiva le notifiche per ricevere messaggi e aggiornamenti in tempo reale, anche quando non stai usando l'app.
            </p>
            <div className="mt-3 flex items-center gap-2">
              <button
                data-testid="push-activate-btn"
                onClick={activate}
                disabled={busy}
                className="px-4 py-2 rounded-full bg-primary text-primary-foreground text-xs uppercase tracking-widest disabled:opacity-50"
              >
                {busy ? "Attivo…" : "Attiva"}
              </button>
              <button
                data-testid="push-later-btn"
                onClick={later}
                className="px-3 py-2 text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground"
              >
                Più tardi
              </button>
            </div>
          </div>
          <button
            onClick={later}
            aria-label="Chiudi"
            className="p-1 rounded-full hover:bg-muted text-muted-foreground flex-shrink-0"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </div>
  );
}
